import api from './client';

export enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  POTENTIAL = 'POTENTIAL',
}

export enum CustomerType {
  INDIVIDUAL = 'INDIVIDUAL',
  BUSINESS = 'BUSINESS',
  BREEDER = 'BREEDER',
  PET_SHOP = 'PET_SHOP',
  AVIARY = 'AVIARY',
  VETERINARIAN = 'VETERINARIAN',
  RESEARCHER = 'RESEARCHER',
}

export enum PreferredContact {
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
}

export interface CustomerMetadata {
  tags?: string[];
  notes?: string;
  preferredBirdTypes?: string[];
  interestCategories?: string[];
  purchaseHistory?: Array<{
    date: string;
    amount: number;
    description: string;
  }>;
  totalPurchases?: number;
  totalSpent?: number;
  lastPurchaseDate?: string;
  averageOrderValue?: number;
  creditRating?: number;
  referralSource?: string;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
}

export interface Customer {
  id: string;
  name: string;
  code: string;
  type: CustomerType;
  status: CustomerStatus;
  firstName?: string;
  lastName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  alternativePhone?: string;
  whatsapp?: string;
  preferredContact: PreferredContact;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  website?: string;
  dateOfBirth?: Date;
  idNumber?: string;
  taxNumber?: string;
  registrationNumber?: string;
  creditLimit?: number;
  currentBalance: number;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  description?: string;
  metadata?: CustomerMetadata;
  createdAt: Date;
  updatedAt: Date;
  fullName: string;
  fullAddress: string;
  isActive: boolean;
  contactInfo: string;
  isPotentialCustomer: boolean;
  isBusinessCustomer: boolean;
}

export interface CreateCustomerDto {
  name: string;
  code: string;
  type: CustomerType;
  status?: CustomerStatus;
  firstName?: string;
  lastName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  alternativePhone?: string;
  whatsapp?: string;
  preferredContact?: PreferredContact;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  website?: string;
  dateOfBirth?: Date;
  idNumber?: string;
  taxNumber?: string;
  registrationNumber?: string;
  creditLimit?: number;
  currentBalance?: number;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  description?: string;
  metadata?: CustomerMetadata;
}

export interface UpdateCustomerDto extends Partial<CreateCustomerDto> {}

export interface CustomerStatistics {
  totalCustomers: number;
  activeCustomers: number;
  inactiveCustomers: number;
  potentialCustomers: number;
  suspendedCustomers: number;
  totalRevenue: number;
  averageOrderValue: number;
  newCustomersThisMonth: number;
  topCustomersByRevenue: Array<{
    id: string;
    name: string;
    totalSpent: number;
  }>;
  customersByType: Record<CustomerType, number>;
  customersByProvince: Record<string, number>;
}

// Utility functions
export const getCustomerStatusColor = (status: CustomerStatus): string => {
  switch (status) {
    case CustomerStatus.ACTIVE:
      return 'green';
    case CustomerStatus.POTENTIAL:
      return 'yellow';
    case CustomerStatus.SUSPENDED:
      return 'red';
    case CustomerStatus.INACTIVE:
      return 'gray';
    default:
      return 'gray';
  }
};

export const getCustomerTypeColor = (type: CustomerType): string => {
  switch (type) {
    case CustomerType.INDIVIDUAL:
      return 'blue';
    case CustomerType.BUSINESS:
      return 'purple';
    case CustomerType.BREEDER:
      return 'green';
    case CustomerType.PET_SHOP:
      return 'orange';
    case CustomerType.AVIARY:
      return 'indigo';
    case CustomerType.VETERINARIAN:
      return 'red';
    case CustomerType.RESEARCHER:
      return 'yellow';
    default:
      return 'gray';
  }
};

export const formatCustomerStatus = (status: CustomerStatus): string => {
  return status.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

export const formatCustomerType = (type: CustomerType): string => {
  return type.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

export const getPreferredContactLabel = (contact: PreferredContact): string => {
  switch (contact) {
    case PreferredContact.EMAIL:
      return 'Email';
    case PreferredContact.PHONE:
      return 'Phone';
    case PreferredContact.SMS:
      return 'SMS';
    case PreferredContact.WHATSAPP:
      return 'WhatsApp';
    default:
      return 'Email';
  }
};

// API functions
export const getAll = async (): Promise<Customer[]> => {
  const response = await api.get<Customer[]>('customers');
  return response.data;
};

export const getById = async (id: string): Promise<Customer> => {
  const response = await api.get<Customer>(`customers/${id}`);
  return response.data;
};

export const getByCode = async (code: string): Promise<Customer> => {
  const response = await api.get<Customer>(`customers/code/${code}`);
  return response.data;
};

export const getByEmail = async (email: string): Promise<Customer[]> => {
  const response = await api.get<Customer[]>(`customers/email/${email}`);
  return response.data;
};

export const getActive = async (): Promise<Customer[]> => {
  const response = await api.get<Customer[]>('customers/active');
  return response.data;
};

export const getPotential = async (): Promise<Customer[]> => {
  const response = await api.get<Customer[]>('customers/potential');
  return response.data;
};

export const getStatistics = async (): Promise<CustomerStatistics> => {
  const response = await api.get<CustomerStatistics>('customers/statistics');
  return response.data;
};

export const search = async (query: string): Promise<Customer[]> => {
  const response = await api.get<Customer[]>(`customers/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

export const create = async (data: CreateCustomerDto): Promise<Customer> => {
  const response = await api.post<Customer>('customers', data);
  return response.data;
};

export const update = async (id: string, data: UpdateCustomerDto): Promise<Customer> => {
  const response = await api.patch<Customer>(`customers/${id}`, data);
  return response.data;
};

export const updateStatus = async (id: string, status: CustomerStatus): Promise<Customer> => {
  const response = await api.patch<Customer>(`customers/${id}/status`, { status });
  return response.data;
};

export const updateBalance = async (
  id: string,
  amount: number,
  operation: 'add' | 'subtract' | 'set'
): Promise<Customer> => {
  const response = await api.patch<Customer>(`customers/${id}/balance`, { amount, operation });
  return response.data;
};

export const remove = async (id: string): Promise<void> => {
  await api.delete(`customers/${id}`);
};

export const getFilteredCustomers = async (params: {
  search?: string;
  type?: CustomerType;
  status?: CustomerStatus;
}): Promise<Customer[]> => {
  const queryParams = new URLSearchParams();
  
  if (params.search) queryParams.append('search', params.search);
  if (params.type) queryParams.append('type', params.type);
  if (params.status) queryParams.append('status', params.status);
  
  const response = await api.get<Customer[]>(`customers?${queryParams.toString()}`);
  return response.data;
};

// Export a customersApi object for consistency with other API modules
export const customersApi = {
  getAll,
  getById,
  getByCode,
  getByEmail,
  getActive,
  getPotential,
  getStatistics,
  search,
  create,
  update,
  updateStatus,
  updateBalance,
  remove,
  getFilteredCustomers,
}; 